// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TextField from "@guestsupply/MobiusOverrides/TextField/TextField";
import Delete from "@guestsupply/XDL/Icons/Delete";
import { UserAccountModel } from "@guestsupply/XDL/types";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

interface OwnProps {
    index: number;
    formId: string;
    userAccount: UserAccountModel;
    removeAddtionalUser: (index: number) => void;
    onChange: (userAccount: UserAccountModel, index: number) => void;
}

type Props = OwnProps;

export interface UserAccountFormStyle {
    removeAdditionalUserLink?: LinkPresentationProps;
    formWrapper?: InjectableCss;
    formFieldsWrapper?: InjectableCss;
    fieldWrapper?: InjectableCss;
    phoneFieldWrapper?: InjectableCss;
    phonesFieldWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    sectionTitle?: TypographyProps;
    phoneNumberLabel?: TypographyProps;
}

export const userAccountFormStyles: UserAccountFormStyle = {};

const styles = userAccountFormStyles;

const UserAccountForm: FC<Props> = ({ userAccount, index, removeAddtionalUser, onChange, formId }) => {
    const firstNameChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedAccount = { ...userAccount, firstName: e.currentTarget.value };
        onChange(updatedAccount, index);
    };
    const lastNameChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedAccount = { ...userAccount, lastName: e.currentTarget.value };
        onChange(updatedAccount, index);
    };
    const emailChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedAccount = { ...userAccount, email: e.currentTarget.value };
        onChange(updatedAccount, index);
    };
    const titleChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedAccount = { ...userAccount, userTitle: e.currentTarget.value };
        onChange(updatedAccount, index);
    };

    const phoneNumberChangeHandler = (e: React.ChangeEvent<HTMLInputElement>, phoneIndex: number) => {
        const enteredPhone = e.currentTarget.value.replace(/\D/g, "");
        const updatedPhone = userAccount.phone;
        updatedPhone[phoneIndex] = enteredPhone;
        const updatedAccount = { ...userAccount, phone: updatedPhone };
        onChange(updatedAccount, index);
    };

    const extensionChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedAccount = { ...userAccount, extension: e.currentTarget.value };
        onChange(updatedAccount, index);
    };

    return (
        <StyledWrapper {...styles.formWrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.sectionTitle}>
                    {`${translate("User Information")} `}
                    {index !== 0 && <Typography>{translate("(Additional Users)")}</Typography>}
                </Typography>

                {index !== 0 && (
                    <StyledWrapper>
                        <Link
                            onClick={() => removeAddtionalUser(index)}
                            {...styles.removeAdditionalUserLink}
                            aria-label={translate("Remove User")}
                        >
                            <Icon src={Delete} />
                        </Link>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.formFieldsWrapper}>
                <StyledWrapper {...styles.fieldWrapper}>
                    <TextField
                        type="text"
                        name={`${index}firstName`}
                        maxLength={25}
                        required
                        label={translate("First Name")}
                        value={userAccount.firstName}
                        onChange={firstNameChangeHandler}
                        {...{ formId }}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.fieldWrapper}>
                    <TextField
                        type="text"
                        name={`${index}lastName`}
                        maxLength={25}
                        required
                        label={translate("Last Name")}
                        value={userAccount.lastName}
                        onChange={lastNameChangeHandler}
                        {...{ formId }}
                    />
                </StyledWrapper>

                <StyledWrapper {...styles.fieldWrapper}>
                    <TextField
                        type="email"
                        name={`${index}email`}
                        maxLength={125}
                        label={translate("Email")}
                        value={userAccount.email}
                        onChange={emailChangeHandler}
                        required
                        {...{
                            formId,
                            validationRules: "required|min:10|max:125|email",
                        }}
                    />
                </StyledWrapper>

                <StyledWrapper {...styles.fieldWrapper}>
                    <TextField
                        type="text"
                        name={`${index}usertitle`}
                        maxLength={25}
                        label={translate("User Title (Optional)")}
                        value={userAccount.userTitle}
                        onChange={titleChangeHandler}
                        {...{ formId }}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.phonesFieldWrapper}>
                    <Typography {...styles.phoneNumberLabel} as="label">
                        {translate("Phone Number")}
                    </Typography>
                    <StyledWrapper {...styles.phoneFieldWrapper}>
                        <TextField
                            type="number"
                            name={`${index}phoneNumber1`}
                            value={userAccount.phone[0]}
                            onChange={e => phoneNumberChangeHandler(e, 0)}
                            required
                            {...{ formId, validationRules: "min:3|max:3", messageLabel: `phone1` }}
                        />
                        <TextField
                            type="number"
                            name={`${index}phoneNumber2`}
                            value={userAccount.phone[1]}
                            onChange={e => phoneNumberChangeHandler(e, 1)}
                            required
                            {...{ formId, validationRules: "min:3|max:3", messageLabel: `phone2` }}
                        />
                        <TextField
                            type="number"
                            name={`${index}phoneNumber3`}
                            value={userAccount.phone[2]}
                            onChange={e => phoneNumberChangeHandler(e, 2)}
                            required
                            {...{ formId, validationRules: "min:4|max:4", messageLabel: `phone3` }}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.fieldWrapper}>
                    <TextField
                        type="number"
                        name={`${index}extension`}
                        maxLength={5}
                        label={translate("Extension (Optional)")}
                        value={userAccount.extension}
                        onChange={extensionChangeHandler}
                        {...{ formId }}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default UserAccountForm;
